/**
 * Hook for managing WebSocket connections to the Upgrade Medic server.
 * Handles three flows:
 *   1. handleConfirmUpdate — main Lambda runtime update (pipeline + manual)
 *   2. handleDeleteStacks — delete CloudFormation stacks for deleted pipelines
 *   3. handleBrazilInterpreterUpdate — Brazil interpreter version upgrades
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import { useCallback, useRef } from 'react';
import {
  AgentLogEntry,
  JobRun,
  JobStage,
  LambdaFunction,
  SelectedVersions,
  DeploymentOptions,
  Account,
  createInitialStages,
  JobStatus
} from '../types';
import { UpgradeMedicDataService } from 'src/services/upgrade-medic/UpgradeMedicDataService';
import { PersistedEphemeralEntry } from './useAgentPanel';
import {
  MessageHandlerDeps,
  classifyLogLevel,
  handleConnectedMessage,
  handleJobStartedMessage,
  handleProgressMessage,
  handleJobBreakdownMessage,
  handleAgentTextMessage,
  handleAgentToolStartMessage,
  handleStageUpdateMessage,
  handleArtifactCreatedMessage,
  handleLogMessage,
  handleJobCompletedMessage,
  handleJobFailedMessage,
  handleJobCancelledMessage,
  handleManualResourcesStartedMessage,
  handleManualResourcesCompletedMessage,
  handleErrorMessage,
  handleDefaultMessage,
  handleMainWsClose,
  stripPipelinePrefix
} from '../utils/wsMessageHandlers';

import { WEBSOCKET_URL, DEFAULT_RESOURCE_REGION, getWebSocketStage } from '../config';

export interface WebSocketDeps {
  userAlias: string;
  selectedLambdas: LambdaFunction[];
  selectedVersions: SelectedVersions;
  deploymentOptions: DeploymentOptions;
  getSelectedPipelines: () => string[];
  clearSelection: () => void;
  currentConfigName: string;
  accounts: Account[];
  // Agent panel
  addLog: (level: AgentLogEntry['level'], message: string, stage?: string, pipelineId?: string) => void;
  addPipelineLog: (pipelineId: string, level: AgentLogEntry['level'], message: string, stage?: string) => void;
  setIsAgentPanelVisible: (visible: boolean) => void;
  setJobStatus: (status: JobStatus) => void;
  jobStatus: JobStatus;
  setProgressMessage: (msg: string) => void;
  setStreamingText: React.Dispatch<React.SetStateAction<string>>;
  streamingTextRef: React.MutableRefObject<string>;
  setPersistedEphemeralEntries: React.Dispatch<React.SetStateAction<PersistedEphemeralEntry[]>>;
  setPipelinePersistedEphemeral: React.Dispatch<React.SetStateAction<Record<string, PersistedEphemeralEntry[]>>>;
  setPipelineStreamingText: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  pipelineStreamingTextRef: React.MutableRefObject<Record<string, string>>;
  setParallelPipelines: React.Dispatch<React.SetStateAction<string[]>>;
  setPipelineLogs: React.Dispatch<React.SetStateAction<Record<string, AgentLogEntry[]>>>;
  setActivePipelineTab: (tab: string) => void;
  parallelPipelines: string[];
  // Job tracking
  setJobHistory: React.Dispatch<React.SetStateAction<JobRun[]>>;
  setCurrentJobId: (id: string | null) => void;
  currentJobIdRef: React.MutableRefObject<string | null>;
  isJobRunningRef: React.MutableRefObject<boolean>;
  jobHistory: JobRun[];
  // Modals
  setIsInstallModalOpen: (open: boolean) => void;
}

/** Build the MessageHandlerDeps subset from WebSocketDeps. */
const toHandlerDeps = (deps: WebSocketDeps): MessageHandlerDeps => ({
  userAlias: deps.userAlias,
  selectedLambdas: deps.selectedLambdas,
  selectedVersions: deps.selectedVersions,
  accounts: deps.accounts,
  currentConfigName: deps.currentConfigName,
  getSelectedPipelines: deps.getSelectedPipelines,
  addLog: deps.addLog,
  addPipelineLog: deps.addPipelineLog,
  setIsAgentPanelVisible: deps.setIsAgentPanelVisible,
  setJobStatus: deps.setJobStatus,
  setProgressMessage: deps.setProgressMessage,
  setStreamingText: deps.setStreamingText,
  streamingTextRef: deps.streamingTextRef,
  setPersistedEphemeralEntries: deps.setPersistedEphemeralEntries,
  setPipelinePersistedEphemeral: deps.setPipelinePersistedEphemeral,
  setPipelineStreamingText: deps.setPipelineStreamingText,
  pipelineStreamingTextRef: deps.pipelineStreamingTextRef,
  setParallelPipelines: deps.setParallelPipelines,
  setPipelineLogs: deps.setPipelineLogs,
  setActivePipelineTab: deps.setActivePipelineTab,
  setJobHistory: deps.setJobHistory,
  setCurrentJobId: deps.setCurrentJobId,
  currentJobIdRef: deps.currentJobIdRef,
  isJobRunningRef: deps.isJobRunningRef,
  jobHistory: deps.jobHistory
});

export const useWebSocketConnection = (
  deps: WebSocketDeps,
  wsRef: React.MutableRefObject<WebSocket | null>,
  wsMapRef: React.MutableRefObject<Map<string, WebSocket>>
) => {
  const depsRef = useRef<WebSocketDeps>(deps);
  depsRef.current = deps;

  // ═══════════════════════════════════════════════════════════
  // 1. MAIN UPDATE FLOW — handleConfirmUpdate
  // ═══════════════════════════════════════════════════════════

  const handleConfirmUpdate = useCallback(() => {
    const {
      addLog,
      addPipelineLog,
      setJobStatus,
      setProgressMessage,
      setIsAgentPanelVisible,
      setStreamingText,
      streamingTextRef,
      setPersistedEphemeralEntries,
      setPipelinePersistedEphemeral,
      setPipelineStreamingText,
      pipelineStreamingTextRef,
      setParallelPipelines,
      setPipelineLogs,
      setActivePipelineTab,
      setJobHistory,
      setCurrentJobId,
      currentJobIdRef,
      isJobRunningRef,
      setIsInstallModalOpen,
      clearSelection,
      getSelectedPipelines,
      selectedLambdas,
      selectedVersions,
      accounts,
      userAlias,
      currentConfigName,
      parallelPipelines
    } = depsRef.current;

    const handlerDeps = toHandlerDeps(depsRef.current);

    setJobStatus('connecting');
    setProgressMessage('Connecting to Upgrade Medic server...');

    // Snapshot selection data before clearing — needed for WS commands sent asynchronously
    const snapshotSelectedLambdas = [...selectedLambdas];
    const snapshotSelectedVersions = { ...selectedVersions };
    const snapshotAccounts = accounts;

    addLog('info', 'Attempting to connect to Upgrade Medic server...');

    const pipelineNames = getSelectedPipelines();

    // Initialize tabs — append to existing
    setParallelPipelines((prev) => {
      const newPipelines = pipelineNames.filter((name) => !prev.includes(name));
      return [...prev, ...newPipelines];
    });
    setPipelineLogs((prev) => {
      const updated = { ...prev };
      pipelineNames.forEach((name: string) => {
        if (!updated[name]) updated[name] = [];
      });
      return updated;
    });
    if (pipelineNames.length > 0) {
      setActivePipelineTab(pipelineNames[0]);
    }

    try {
      const ws = new WebSocket(WEBSOCKET_URL);
      // Track this WS: set as primary if none active, register in map once jobId is known
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        wsRef.current = ws;
      }
      let wsJobId: string | null = null;

      const timeout = setTimeout(() => {
        ws.close();
        setJobStatus('idle');
        setProgressMessage('Connection timeout');
        addLog('error', '❌ Connection timeout. Server not responding.');
        setIsInstallModalOpen(true);
      }, 5000);

      ws.onopen = () => {
        clearTimeout(timeout);
        clearSelection();
        addLog('info', '✅ WebSocket connection established');
      };

      ws.onmessage = (event) => {
        console.log('[Upgrade Medic] Raw message:', event.data);
        try {
          const message = JSON.parse(event.data);
          console.log('[Upgrade Medic] Parsed message type:', message.type, message);

          // Refresh handlerDeps for each message to get latest state via refs
          const latestDeps = toHandlerDeps(depsRef.current);

          // Detect manual-only flow to route non-pipeline-scoped messages to the correct tab
          const isManualFlow = pipelineNames.every((name) => name === 'Manually created' || name === 'Manual Deployed Stacks');
          const manualPipelineId = isManualFlow && pipelineNames.length > 0 ? pipelineNames[0] : null;

          switch (message.type) {
            case 'connected':
              handleConnectedMessage(message, ws, pipelineNames, {
                ...latestDeps,
                selectedLambdas: snapshotSelectedLambdas,
                selectedVersions: snapshotSelectedVersions,
                accounts: snapshotAccounts,
                ...({ deployToAlpha: depsRef.current.deploymentOptions.deployToAlpha } as any)
              });
              break;
            case 'job_started':
              wsJobId = message.jobId;
              if (wsJobId) {
                wsMapRef.current.set(wsJobId, ws);
              }
              handleJobStartedMessage(message, pipelineNames, {
                ...latestDeps,
                selectedLambdas: snapshotSelectedLambdas,
                selectedVersions: snapshotSelectedVersions,
                accounts: snapshotAccounts,
                ...({ deployToAlpha: depsRef.current.deploymentOptions.deployToAlpha } as any)
              });
              break;
            case 'progress':
              handleProgressMessage(message, latestDeps);
              break;
            case 'job_breakdown':
              handleJobBreakdownMessage(message, latestDeps);
              break;
            case 'pipeline_started':
              addPipelineLog(message.pipelineId, 'phase', '▶️ Pipeline started');
              break;
            case 'pipeline_completed':
              addPipelineLog(message.pipelineId, 'phase', '✅ Pipeline completed successfully');
              break;
            case 'pipeline_failed':
              addPipelineLog(message.pipelineId, 'error', `❌ Pipeline failed: ${message.error || 'Unknown error'}`);
              break;
            case 'agent_text':
              handleAgentTextMessage(message, latestDeps);
              break;
            case 'agent_reasoning':
              handleAgentTextMessage({ ...message, text: message.data?.content || message.text || '' }, latestDeps);
              break;
            case 'agent_tool_start':
              handleAgentToolStartMessage(message, parallelPipelines, latestDeps);
              break;
            case 'agent_tool_result':
              break;
            case 'agent_complete':
              setStreamingText('');
              if (message.pipelineId) {
                addPipelineLog(
                  message.pipelineId,
                  'phase',
                  `✅ Agent completed: ${message.stats?.tool_calls || 0} tool calls, ${message.stats?.cycles || 0} cycles`
                );
              } else {
                const agentCompleteMsg = `✅ Agent completed: ${message.stats?.tool_calls || 0} tool calls, ${message.stats?.cycles || 0} cycles`;
                addLog('phase', agentCompleteMsg);
                if (manualPipelineId) {
                  addPipelineLog(manualPipelineId, 'phase', agentCompleteMsg);
                }
              }
              break;
            case 'agent_status':
              break;
            case 'stage_update':
              handleStageUpdateMessage(message, {
                ...latestDeps,
                selectedLambdas: snapshotSelectedLambdas,
                selectedVersions: snapshotSelectedVersions,
                accounts: snapshotAccounts
              });
              break;
            case 'artifact_created':
              handleArtifactCreatedMessage(message, latestDeps);
              break;
            case 'stage_start':
              if (message.pipelineId) {
                addPipelineLog(message.pipelineId, 'phase', `Stage: ${message.stage} - ${message.message || 'Starting...'}`);
              } else {
                const stageStartMsg = `Stage: ${message.stage} - ${message.message || 'Starting...'}`;
                addLog('phase', stageStartMsg);
                if (manualPipelineId) {
                  addPipelineLog(manualPipelineId, 'phase', stageStartMsg);
                }
              }
              setProgressMessage(`Stage: ${message.stage}`);
              // Update currentStage and persist to DDB
              if (message.stage) {
                const stageJobId = message.jobId || latestDeps.currentJobIdRef.current;
                if (stageJobId) {
                  latestDeps.setJobHistory((prev) =>
                    prev.map((job) =>
                      job.jobId === stageJobId
                        ? {
                            ...job,
                            currentStage: message.stage,
                            stages: {
                              ...job.stages,
                              [message.stage]: {
                                ...job.stages?.[message.stage],
                                status: 'running' as const,
                                startedAt: job.stages?.[message.stage]?.startedAt || new Date().toISOString(),
                                message: message.message || 'Starting...'
                              }
                            }
                          }
                        : job
                    )
                  );
                  UpgradeMedicDataService.updateJobStage(
                    stageJobId,
                    message.stage,
                    { status: 'running', startedAt: new Date().toISOString(), message: message.message || 'Starting...' },
                    0,
                    message.message || 'Starting...'
                  ).catch((err) => console.error('[Upgrade Medic] Failed to persist stage_start:', err));
                }
              }
              break;
            case 'stage_end':
              if (message.pipelineId) {
                if (message.success) {
                  addPipelineLog(message.pipelineId, 'phase', `✅ Stage ${message.stage}: Completed`);
                } else {
                  addPipelineLog(message.pipelineId, 'error', `❌ Stage ${message.stage}: Failed - ${message.error || 'Unknown error'}`);
                }
              } else {
                if (message.success) {
                  const endMsg = `✅ Stage ${message.stage}: Completed`;
                  addLog('phase', endMsg);
                  if (manualPipelineId) addPipelineLog(manualPipelineId, 'phase', endMsg);
                } else {
                  const endMsg = `❌ Stage ${message.stage}: Failed - ${message.error || 'Unknown error'}`;
                  addLog('error', endMsg);
                  if (manualPipelineId) addPipelineLog(manualPipelineId, 'error', endMsg);
                }
              }
              // Update stage completion in job and persist to DDB
              if (message.stage) {
                const endJobId = message.jobId || latestDeps.currentJobIdRef.current;
                const endStatus = message.success ? 'completed' : 'failed';
                if (endJobId) {
                  latestDeps.setJobHistory((prev) =>
                    prev.map((job) =>
                      job.jobId === endJobId
                        ? {
                            ...job,
                            stages: {
                              ...job.stages,
                              [message.stage]: {
                                ...job.stages?.[message.stage],
                                status: endStatus as any,
                                completedAt: new Date().toISOString(),
                                message: message.success ? 'Completed' : message.error || 'Failed'
                              }
                            }
                          }
                        : job
                    )
                  );
                  UpgradeMedicDataService.updateJobStage(
                    endJobId,
                    message.stage,
                    {
                      status: endStatus as any,
                      startedAt: undefined,
                      completedAt: new Date().toISOString(),
                      message: message.success ? 'Completed' : message.error || 'Failed'
                    },
                    0,
                    message.success ? 'Completed' : message.error || 'Failed'
                  ).catch((err) => console.error('[Upgrade Medic] Failed to persist stage_end:', err));
                }
              }
              break;
            case 'workflow_step':
              if (message.pipelineId) {
                addPipelineLog(message.pipelineId, 'phase', `📌 ${message.step}: ${message.message}`);
              } else {
                const stepMsg = `📌 ${message.step}: ${message.message}`;
                addLog('phase', stepMsg);
                if (manualPipelineId) addPipelineLog(manualPipelineId, 'phase', stepMsg);
              }
              break;
            case 'log':
              // For manual flows, inject pipelineId so logs appear in the pipeline tab
              if (!message.pipelineId && manualPipelineId) {
                handleLogMessage({ ...message, pipelineId: manualPipelineId }, latestDeps);
              } else {
                handleLogMessage(message, latestDeps);
              }
              break;
            case 'output':
              if (message.pipelineId) {
                addPipelineLog(message.pipelineId, 'agent', message.data || message.message || JSON.stringify(message));
              } else if (manualPipelineId) {
                addPipelineLog(manualPipelineId, 'agent', message.data || message.message || JSON.stringify(message));
              } else {
                addLog('agent', message.data || message.message || JSON.stringify(message));
              }
              break;
            case 'job_completed':
              handleJobCompletedMessage(message, {
                ...latestDeps,
                selectedLambdas: snapshotSelectedLambdas,
                selectedVersions: snapshotSelectedVersions,
                accounts: snapshotAccounts
              });
              break;
            case 'job_failed':
              handleJobFailedMessage(message, {
                ...latestDeps,
                selectedLambdas: snapshotSelectedLambdas,
                selectedVersions: snapshotSelectedVersions,
                accounts: snapshotAccounts
              });
              break;
            case 'job_cancelled':
              handleJobCancelledMessage(message, latestDeps);
              break;
            case 'manual_resources_update_started':
              if (!wsJobId && message.jobId) {
                wsJobId = message.jobId;
                wsMapRef.current.set(message.jobId, ws);
              }
              handleManualResourcesStartedMessage(message, pipelineNames, {
                ...latestDeps,
                selectedLambdas: snapshotSelectedLambdas,
                selectedVersions: snapshotSelectedVersions,
                accounts: snapshotAccounts
              });
              break;
            case 'manual_resources_update_completed':
              handleManualResourcesCompletedMessage(message, {
                ...latestDeps,
                selectedLambdas: snapshotSelectedLambdas,
                selectedVersions: snapshotSelectedVersions,
                accounts: snapshotAccounts
              });
              break;
            case 'manual_update_progress':
            case 'manual_update_stage_update': {
              // Render manual update stage/progress events as log entries
              const manualMsg = message.message || message.data || '';
              if (manualMsg) {
                const manualLogLevel: AgentLogEntry['level'] =
                  manualMsg.includes('❌') || manualMsg.includes('failed')
                    ? 'error'
                    : manualMsg.includes('⚠️')
                    ? 'warning'
                    : manualMsg.includes('✅')
                    ? 'phase'
                    : 'agent';
                if (manualPipelineId) {
                  addPipelineLog(manualPipelineId, manualLogLevel, manualMsg);
                } else {
                  addLog(manualLogLevel, manualMsg);
                }
              }
              break;
            }
            case 'manual_update_completed':
              // Suppressed — handled by manual_resources_update_completed
              break;
            case 'status':
              addLog('info', `Status: ${message.status} | Active jobs: ${message.active_jobs}`);
              break;
            case 'pong':
              break;
            case 'error':
              handleErrorMessage(message, latestDeps);
              break;
            default:
              handleDefaultMessage(message, latestDeps);
          }
        } catch {
          const rawData = event.data;
          addLog(classifyLogLevel(rawData), rawData);
        }
      };

      ws.onerror = () => {
        clearTimeout(timeout);
        setJobStatus('idle');
        setProgressMessage('Connection failed');
        addLog('error', '❌ Failed to connect to Upgrade Medic server');
        setIsInstallModalOpen(true);
      };

      ws.onclose = () => {
        // Clean up this WS from the map
        if (wsJobId) {
          wsMapRef.current.delete(wsJobId);
        }
        // Only clear wsRef if this WS is the current primary
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
        handleMainWsClose(toHandlerDeps(depsRef.current));
      };
    } catch {
      setJobStatus('idle');
      setProgressMessage('Connection error');
      addLog('error', '❌ Failed to establish WebSocket connection');
      setIsInstallModalOpen(true);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════
  // 2. DELETE STACKS FLOW
  // ═══════════════════════════════════════════════════════════

  const handleDeleteStacks = useCallback(
    (
      deletedPipelineNames: string[],
      selectedLambdasWithPipeline: Array<{ pipelineName: string; lambda: LambdaFunction; isManual?: boolean }>,
      manualLambdaArns?: Array<{ arn: string; name: string; region: string }>
    ) => {
      const {
        addLog,
        setIsAgentPanelVisible,
        setJobStatus,
        setProgressMessage,
        setJobHistory,
        setIsInstallModalOpen,
        clearSelection,
        userAlias,
        jobStatus,
        isJobRunningRef
      } = depsRef.current;

      const hasStacks = deletedPipelineNames.length > 0;
      const hasLambdas = manualLambdaArns && manualLambdaArns.length > 0;
      const totalResources = deletedPipelineNames.length + (manualLambdaArns?.length || 0);

      const deleteDescription = [
        hasStacks ? `${deletedPipelineNames.length} CloudFormation stack(s)` : '',
        hasLambdas ? `${manualLambdaArns!.length} standalone Lambda function(s)` : ''
      ]
        .filter(Boolean)
        .join(' and ');

      addLog('info', `🗑️ Preparing to delete ${deleteDescription}...`);
      setIsAgentPanelVisible(true);
      setJobStatus('connecting');
      setProgressMessage(`Connecting to delete ${deleteDescription}...`);

      // Track completion of both delete commands when both are sent
      let stackDeleteResult: { completed: boolean; summary?: any; results?: any[]; jobId?: string } = { completed: !hasStacks };
      let lambdaDeleteResult: { completed: boolean; summary?: any; results?: any[]; jobId?: string } = { completed: !hasLambdas };

      const checkAllCompleted = () => {
        if (!stackDeleteResult.completed || !lambdaDeleteResult.completed) return;

        const stackSuccess = stackDeleteResult.summary?.success || 0;
        const stackFailed = stackDeleteResult.summary?.failed || 0;
        const lambdaSuccess = lambdaDeleteResult.summary?.success || 0;
        const lambdaFailed = lambdaDeleteResult.summary?.failed || 0;

        const totalSuccess = stackSuccess + lambdaSuccess;
        const totalFailed = stackFailed + lambdaFailed;
        const totalCount = totalSuccess + totalFailed;
        const hasFailures = totalFailed > 0;
        const deleteStatus: JobRun['status'] = hasFailures ? 'failed' : 'completed';

        if (!hasFailures) {
          setJobStatus('completed');
          addLog('phase', `✅ Deletion completed: ${totalSuccess}/${totalCount} succeeded`);
        } else {
          setJobStatus('failed');
          addLog('error', `❌ Deletion completed with errors: ${totalSuccess}/${totalCount} succeeded`);
        }

        const allResults = [...(stackDeleteResult.results || []), ...(lambdaDeleteResult.results || [])];
        const failedResults = allResults.filter((r: any) => !r.success);
        const errorMessage =
          failedResults.length > 0
            ? failedResults.map((r: any) => `${r.stackName || r.functionName || r.arn}: ${r.error || 'Failed'}`).join('; ')
            : hasFailures
            ? 'Deletion failed'
            : '';

        const deleteJob: JobRun = {
          jobId: stackDeleteResult.jobId || lambdaDeleteResult.jobId || `delete_${Date.now()}`,
          pipelineId: 'Delete Resources',
          status: deleteStatus,
          triggeredBy: userAlias,
          startedAt: new Date(),
          completedAt: new Date(),
          pipelines: deletedPipelineNames,
          functions: manualLambdaArns?.map((l) => l.name) || [],
          targetVersions: {},
          resourceType: 'Lambda',
          progress: 100,
          progressMessage: hasFailures ? errorMessage : `Deleted ${totalSuccess} resource(s)`,
          currentStage: hasFailures ? JobStage.FAILED : JobStage.COMPLETED,
          stages: {},
          artifacts: {
            ...(hasStacks ? { stackNames: deletedPipelineNames } : {}),
            ...(hasLambdas ? { lambdaArns: manualLambdaArns!.map((l) => l.arn) } : {})
          },
          error: hasFailures ? { code: 'DELETE_RESOURCES_FAILED', message: errorMessage, stage: JobStage.BUILD, retryable: true } : undefined,
          totalResources: totalCount || totalResources,
          logs: [],
          streamingText: '',
          result: {
            success: totalSuccess,
            failed: totalFailed,
            total: totalCount || totalResources
          }
        };

        setJobHistory((prev) => [...prev, deleteJob]);
        UpgradeMedicDataService.createJobRun(deleteJob).catch((err) =>
          console.error('[Upgrade Medic] Failed to persist delete job to DynamoDB:', err)
        );
        clearSelection();
      };

      try {
        const ws = new WebSocket(WEBSOCKET_URL);
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          wsRef.current = ws;
        }
        let wsJobId: string | null = null;

        ws.onopen = () => {
          addLog('info', '✅ WebSocket connection established');
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);

            if (message.type === 'connected') {
              // Send delete_stacks command if there are stacks to delete
              if (hasStacks) {
                const deleteStacksCommand = {
                  type: 'delete_stacks',
                  data: {
                    userId: userAlias,
                    stage: getWebSocketStage(),
                    stacks: deletedPipelineNames.map((name) => {
                      const lambdasInPipeline = selectedLambdasWithPipeline.filter((l) => l.pipelineName === name || l.lambda.stackName === name);
                      const region = lambdasInPipeline[0]?.lambda?.region || DEFAULT_RESOURCE_REGION;
                      return { stackName: name, region };
                    })
                  }
                };
                ws.send(JSON.stringify(deleteStacksCommand));
                addLog('phase', `🗑️ Sent delete request for ${deletedPipelineNames.length} stack(s): ${deletedPipelineNames.join(', ')}`);
              }

              // Send delete_lambdas command if there are standalone lambdas to delete
              if (hasLambdas) {
                const deleteLambdasCommand = {
                  type: 'delete_lambdas',
                  data: {
                    userId: userAlias,
                    stage: getWebSocketStage(),
                    lambdas: manualLambdaArns!.map((l) => ({
                      arn: l.arn,
                      name: l.name,
                      region: l.region
                    }))
                  }
                };
                ws.send(JSON.stringify(deleteLambdasCommand));
                addLog(
                  'phase',
                  `🗑️ Sent delete request for ${manualLambdaArns!.length} Lambda function(s): ${manualLambdaArns!.map((l) => l.name).join(', ')}`
                );
              }

              setJobStatus('running');
            } else if (message.type === 'delete_stack_completed') {
              if (!wsJobId && message.jobId) {
                wsJobId = message.jobId;
              }

              if (message.results && message.results.length > 0) {
                message.results.forEach((result: any) => {
                  if (result.success) {
                    addLog('info', `   ✓ ${result.stackName}: Deleted successfully`);
                  } else {
                    addLog('error', `   ✗ ${result.stackName}: ${result.error || 'Failed'}`);
                  }
                });
              }

              stackDeleteResult = {
                completed: true,
                summary: message.summary || { success: 0, failed: 0, total: deletedPipelineNames.length },
                results: message.results || [],
                jobId: message.jobId
              };
              checkAllCompleted();
            } else if (message.type === 'delete_lambdas_completed') {
              if (!wsJobId && message.jobId) {
                wsJobId = message.jobId;
              }

              if (message.results && message.results.length > 0) {
                message.results.forEach((result: any) => {
                  if (result.success) {
                    addLog('info', `   ✓ ${result.functionName || result.arn}: Deleted successfully`);
                  } else {
                    addLog('error', `   ✗ ${result.functionName || result.arn}: ${result.error || 'Failed'}`);
                  }
                });
              }

              lambdaDeleteResult = {
                completed: true,
                summary: message.summary || { success: 0, failed: 0, total: manualLambdaArns?.length || 0 },
                results: message.results || [],
                jobId: message.jobId
              };
              checkAllCompleted();
            } else if (message.type === 'delete_stack_failed') {
              addLog('error', `❌ Stack deletion failed: ${message.error}`);
              stackDeleteResult = {
                completed: true,
                summary: { success: 0, failed: deletedPipelineNames.length, total: deletedPipelineNames.length },
                results: [],
                jobId: message.jobId
              };
              checkAllCompleted();
            } else if (message.type === 'delete_lambdas_failed') {
              addLog('error', `❌ Lambda deletion failed: ${message.error}`);
              lambdaDeleteResult = {
                completed: true,
                summary: { success: 0, failed: manualLambdaArns?.length || 0, total: manualLambdaArns?.length || 0 },
                results: [],
                jobId: message.jobId
              };
              checkAllCompleted();
            } else if (message.type === 'log') {
              addLog(message.level || 'info', message.message);
            }
          } catch {
            addLog('agent', event.data);
          }
        };

        ws.onerror = () => {
          setJobStatus('failed');
          addLog('error', '❌ Failed to connect to Upgrade Medic server');
          setIsInstallModalOpen(true);
        };

        ws.onclose = () => {
          // Clean up this WS from the map
          if (wsJobId) {
            wsMapRef.current.delete(wsJobId);
          }
          // Only clear wsRef if this WS is the current primary
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
          if (jobStatus === 'running') {
            setJobStatus('failed');
            addLog('error', '❌ Connection closed unexpectedly');
          }
        };
      } catch (error) {
        setJobStatus('failed');
        addLog('error', `❌ Failed to establish connection: ${error}`);
        setIsInstallModalOpen(true);
      }
    },
    []
  );

  // ═══════════════════════════════════════════════════════════
  // 3. BRAZIL INTERPRETER UPDATE FLOW
  // ═══════════════════════════════════════════════════════════

  const handleBrazilInterpreterUpdate = useCallback(
    (selectedBrazilPipelines: Set<string>, selectedBrazilVersions: { cpython?: string; nodejs?: string }, clearBrazilSelection: () => void) => {
      const {
        addLog,
        setJobStatus,
        setProgressMessage,
        setIsAgentPanelVisible,
        setStreamingText,
        setParallelPipelines,
        setPipelineLogs,
        setActivePipelineTab,
        setJobHistory,
        setCurrentJobId,
        currentJobIdRef,
        isJobRunningRef,
        setIsInstallModalOpen,
        userAlias
      } = depsRef.current;

      setJobStatus('connecting');
      setProgressMessage('Connecting to Upgrade Medic server...');
      addLog('info', 'Attempting to connect to Upgrade Medic server...');

      const pipelineNames = Array.from(selectedBrazilPipelines);
      setParallelPipelines((prev) => {
        const newPipelines = pipelineNames.filter((name) => !prev.includes(name));
        return [...prev, ...newPipelines];
      });
      setPipelineLogs((prev) => {
        const updated = { ...prev };
        pipelineNames.forEach((name: string) => {
          if (!updated[name]) updated[name] = [];
        });
        return updated;
      });
      if (pipelineNames.length > 0) {
        setActivePipelineTab(pipelineNames[0]);
      }

      try {
        const ws = new WebSocket(WEBSOCKET_URL);
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          wsRef.current = ws;
        }
        let wsJobId: string | null = null;

        const timeout = setTimeout(() => {
          ws.close();
          setJobStatus('failed');
          setProgressMessage('Connection timeout');
          addLog('error', '❌ Connection timeout. Server not responding.');
          setIsInstallModalOpen(true);
        }, 5000);

        ws.onopen = () => {
          clearTimeout(timeout);
          addLog('info', '✅ WebSocket connection established');
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);

            if (message.type === 'connected') {
              addLog('phase', `✅ ${message.message} (v${message.server_version})`);
              setIsAgentPanelVisible(true);

              const updateCommand = {
                type: 'start_update',
                data: {
                  userId: userAlias,
                  stage: getWebSocketStage(),
                  pipelines: Array.from(selectedBrazilPipelines),
                  updateType: 'interpreter_version' as const,
                  targetVersions: {
                    python: selectedBrazilVersions.cpython
                      ? `python${selectedBrazilVersions.cpython.replace('CPython', '').replace(/(\d)(\d+)/, '$1.$2')}`
                      : undefined,
                    nodejs: selectedBrazilVersions.nodejs
                      ? `nodejs${selectedBrazilVersions.nodejs.replace('NodeJS-', '').replace('.x', '.x')}`
                      : undefined
                  },
                  targetInterpreterVersions: {
                    CPython3: selectedBrazilVersions.cpython
                      ? selectedBrazilVersions.cpython.replace('CPython', '').replace(/(\d)(\d+)/, '$1.$2')
                      : undefined,
                    NodeJS: selectedBrazilVersions.nodejs ? selectedBrazilVersions.nodejs.replace('NodeJS-', '').replace('.x', '') : undefined
                  }
                }
              };
              ws.send(JSON.stringify(updateCommand));
              addLog('phase', `🚀 Sent interpreter update for ${selectedBrazilPipelines.size} pipeline(s)`);
              clearBrazilSelection();
            } else if (message.type === 'job_started') {
              wsJobId = message.jobId;
              if (wsJobId) {
                wsMapRef.current.set(wsJobId, ws);
              }
              handleBrazilJobStarted(message, pipelineNames, selectedBrazilVersions);
            } else if (message.type === 'stage_update') {
              handleBrazilStageUpdate(message);
            } else if (message.type === 'artifact_created') {
              handleBrazilArtifactCreated(message);
            } else if (message.type === 'job_completed') {
              handleBrazilJobCompleted(message);
            } else if (message.type === 'job_failed') {
              handleBrazilJobFailed(message);
            } else if (message.type === 'agent_text' || message.type === 'agent_reasoning') {
              const text = message.type === 'agent_reasoning' ? message.data?.content || message.text || '' : message.text;
              setStreamingText((prev) => prev + text);
            } else if (message.type === 'agent_tool_start') {
              setStreamingText('');
              addLog('tool', `🔧 [Tool] ${message.tool}: ${message.display}`);
            } else if (message.type === 'agent_complete') {
              setStreamingText('');
              addLog('phase', `✅ Agent completed: ${message.stats?.tool_calls || 0} tool calls`);
            } else if (message.type === 'output') {
              addLog('agent', message.data || message.message || JSON.stringify(message));
            } else if (message.type === 'log') {
              handleBrazilLogMessage(message);
            } else if (message.type === 'progress') {
              if (message.progress !== undefined && message.message !== undefined) {
                setProgressMessage(`${message.progress}% - ${message.message}`);
                addLog('agent', `[${message.progress}%] ${message.message}`);
              }
            } else {
              const content = message.message || message.data || JSON.stringify(message);
              addLog('agent', content);
            }
          } catch {
            addLog('agent', event.data);
          }
        };

        ws.onerror = () => {
          clearTimeout(timeout);
          setJobStatus('failed');
          setProgressMessage('Connection failed');
          addLog('error', '❌ Failed to connect to Upgrade Medic server');
          setIsInstallModalOpen(true);
        };

        ws.onclose = () => {
          // Clean up this WS from the map
          if (wsJobId) {
            wsMapRef.current.delete(wsJobId);
          }
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
          if (isJobRunningRef.current) {
            isJobRunningRef.current = false;
            setJobStatus('failed');
            addLog('error', '❌ Server disconnected unexpectedly');
          }
        };
      } catch {
        setJobStatus('failed');
        setProgressMessage('Connection error');
        addLog('error', '❌ Failed to establish WebSocket connection');
        setIsInstallModalOpen(true);
      }
    },
    []
  );

  // ─── Brazil sub-handlers (used only within handleBrazilInterpreterUpdate) ───

  const handleBrazilJobStarted = useCallback(
    (message: any, pipelineNames: string[], selectedBrazilVersions: { cpython?: string; nodejs?: string }) => {
      const { setJobStatus, setProgressMessage, isJobRunningRef, setCurrentJobId, currentJobIdRef, setJobHistory, addLog, userAlias } =
        depsRef.current;

      setJobStatus('running');
      isJobRunningRef.current = true;
      setCurrentJobId(message.jobId);
      currentJobIdRef.current = message.jobId;
      setProgressMessage(message.message || 'Brazil interpreter update started');

      const jobPipelineId = message.pipelineId || message.pipeline || (message.pipelines?.length === 1 ? message.pipelines[0] : undefined);
      const jobTotalResources = message.totalResources ?? message.functionCount ?? pipelineNames.length;

      const newJob: JobRun = {
        jobId: message.jobId,
        pipelineId: jobPipelineId,
        status: 'running',
        triggeredBy: userAlias,
        startedAt: new Date(),
        pipelines: jobPipelineId ? [jobPipelineId] : pipelineNames,
        functions: [],
        targetVersions: {
          ...(selectedBrazilVersions.cpython && { cpython: selectedBrazilVersions.cpython }),
          ...(selectedBrazilVersions.nodejs && { nodejs: selectedBrazilVersions.nodejs })
        },
        resourceType: 'Lambda',
        progress: 0,
        progressMessage: message.message || 'Brazil interpreter update started',
        currentStage: JobStage.INITIALIZING,
        stages: createInitialStages(),
        artifacts: {},
        totalResources: jobTotalResources,
        logs: [],
        streamingText: ''
      };
      setJobHistory((prev: JobRun[]) => [...prev, newJob]);
      UpgradeMedicDataService.createJobRun(newJob).catch((err: Error) =>
        console.error('[Upgrade Medic] Failed to persist Brazil interpreter job to DynamoDB:', err)
      );
      addLog('phase', `📋 Job started: ${message.jobId}`);
      addLog('info', message.message || 'Brazil interpreter update in progress');
    },
    []
  );

  const handleBrazilStageUpdate = useCallback((message: any) => {
    const { isJobRunningRef, setJobHistory, setProgressMessage, addLog } = depsRef.current;

    if (!message.stage || !message.jobId) {
      console.log('[Upgrade Medic] Skipping stage_update with missing data:', message);
      return;
    }
    if (!isJobRunningRef.current) {
      console.log('[Upgrade Medic] Skipping stage_update after job completed:', message.stage);
      return;
    }

    const stageProgress = message.overallProgress ?? message.progress;

    setJobHistory((prev: JobRun[]) =>
      prev.map((job: JobRun) =>
        job.jobId === message.jobId
          ? {
              ...job,
              currentStage: message.stage as JobStage,
              progress: stageProgress ?? job.progress,
              progressMessage: message.stageMessage || job.progressMessage,
              stages: {
                ...job.stages,
                [message.stage]: {
                  status: message.stageStatus,
                  startedAt: message.stageStartedAt,
                  completedAt: message.stageCompletedAt,
                  message: message.stageMessage
                }
              },
              artifacts: message.artifacts || job.artifacts
            }
          : job
      )
    );

    // Compute display percentage: use backend value if present, otherwise derive from stage index
    let displayPercent = stageProgress;
    if (displayPercent == null) {
      const stageOrder = [
        JobStage.INITIALIZING,
        JobStage.IMPACT_ANALYSIS,
        JobStage.CODE_CHANGES,
        JobStage.BUILD,
        JobStage.PIPELINE_TRIGGER,
        JobStage.CLEANUP
      ];
      const idx = stageOrder.indexOf(message.stage as JobStage);
      if (idx >= 0) {
        displayPercent = Math.round(((idx + 1) / stageOrder.length) * 100);
      } else {
        displayPercent = 0;
      }
    }

    setProgressMessage(`[${displayPercent}%] ${message.stage}: ${message.stageMessage || 'Processing...'}`);
    addLog('phase', `Stage: ${message.stage} - ${message.stageMessage || 'Processing...'}`);

    UpgradeMedicDataService.updateJobStage(
      message.jobId,
      message.stage as JobStage,
      {
        status: message.stageStatus,
        startedAt: message.stageStartedAt,
        completedAt: message.stageCompletedAt,
        message: message.stageMessage
      },
      displayPercent,
      message.stageMessage || ''
    ).catch((err: Error) => console.error('[Upgrade Medic] Failed to persist stage update:', err));
  }, []);

  const handleBrazilArtifactCreated = useCallback((message: any) => {
    const { setJobHistory, addLog } = depsRef.current;

    setJobHistory((prev: JobRun[]) =>
      prev.map((job: JobRun) =>
        job.jobId === message.jobId ? { ...job, artifacts: { ...job.artifacts, [message.artifactType]: message.artifactUrl } } : job
      )
    );
    addLog('tool', `🔗 ${message.artifactType}: ${message.artifactTitle || message.artifactUrl}`);

    UpgradeMedicDataService.updateJobArtifact(
      message.jobId,
      message.artifactType as 'buildLink' | 'crLink' | 'pipelineLink',
      message.artifactUrl
    ).catch((err: Error) => console.error('[Upgrade Medic] Failed to persist artifact:', err));
  }, []);

  const handleBrazilJobCompleted = useCallback((message: any) => {
    const { setJobStatus, isJobRunningRef, setProgressMessage, setJobHistory, addLog } = depsRef.current;

    const completedStatus: JobRun['status'] = message.summary?.failed > 0 ? 'failed' : 'completed';
    setJobStatus(completedStatus);
    isJobRunningRef.current = false;
    setProgressMessage(message.summary ? `Completed: ${message.summary.success}/${message.summary.total} succeeded` : 'Job completed');

    setJobHistory((prev: JobRun[]) =>
      prev.map((job: JobRun) => {
        if (job.jobId === message.jobId) {
          const completedArtifacts = {
            ...job.artifacts,
            crLink: message.crLink || message.artifacts?.crLink || job.artifacts?.crLink,
            buildLink: message.buildLink || message.artifacts?.buildLink || job.artifacts?.buildLink,
            manualActions: message.manualActions || message.artifacts?.manualActions || job.artifacts?.manualActions,
            pipelineLink: message.pipelineLink || message.artifacts?.pipelineLink || job.artifacts?.pipelineLink
          };

          const completedJob: JobRun = {
            ...job,
            status: completedStatus,
            completedAt: new Date(),
            artifacts: completedArtifacts,
            result: message.summary ? { success: message.summary.success, failed: message.summary.failed, total: message.summary.total } : undefined
          };

          UpgradeMedicDataService.completeJobRun(
            job.jobId,
            completedStatus,
            job.stages,
            completedArtifacts,
            message.summary ? { success: message.summary.success, failed: message.summary.failed, total: message.summary.total } : undefined
          ).catch((err: Error) => console.error('[Upgrade Medic] Failed to persist Brazil interpreter job completion:', err));

          return completedJob;
        }
        return job;
      })
    );
    addLog('phase', '✅ Job completed');
  }, []);

  const handleBrazilJobFailed = useCallback((message: any) => {
    const { setJobStatus, isJobRunningRef, setProgressMessage, currentJobIdRef, setJobHistory, addLog } = depsRef.current;

    setJobStatus('failed');
    isJobRunningRef.current = false;
    const brazilPipelineName = message.pipelineId || message.pipeline_id || message.pipeline || '';
    const rawError = message.error || message.message || 'Unknown error';
    const errorMsg = stripPipelinePrefix(rawError, brazilPipelineName);
    setProgressMessage(`Failed: ${errorMsg}`);

    setJobHistory((prev: JobRun[]) =>
      prev.map((job: JobRun) => {
        if (job.jobId === (message.jobId || currentJobIdRef.current)) {
          const failedJob: JobRun = {
            ...job,
            status: 'failed' as const,
            completedAt: new Date(),
            progressMessage: errorMsg,
            error: {
              code: message.error_code || 'JOB_FAILED',
              message: errorMsg,
              stage: message.failedStage || job.currentStage || JobStage.INITIALIZING,
              retryable: message.retryable ?? false
            }
          };

          UpgradeMedicDataService.completeJobRun(job.jobId, 'failed', job.stages, job.artifacts, undefined, {
            code: message.error_code || 'JOB_FAILED',
            message: errorMsg,
            stage: message.failedStage || job.currentStage || JobStage.INITIALIZING,
            retryable: message.retryable ?? false
          }).catch((err: Error) => console.error('[Upgrade Medic] Failed to persist Brazil interpreter job failure:', err));

          return failedJob;
        }
        return job;
      })
    );
    addLog('error', `❌ Job failed: ${errorMsg}`);
  }, []);

  const handleBrazilLogMessage = useCallback((message: any) => {
    const { addLog } = depsRef.current;
    const logLevel = message.level || 'info';
    const logMessage = message.message || message.data || JSON.stringify(message);
    let logType: AgentLogEntry['level'] = 'agent';
    if (logLevel === 'error' || logMessage.includes('❌') || logMessage.includes('Failed')) {
      logType = 'error';
    } else if (logLevel === 'warning' || logMessage.includes('⚠️')) {
      logType = 'warning';
    } else if (logMessage.includes('✅') || logMessage.includes('Success')) {
      logType = 'phase';
    }
    addLog(logType, logMessage);
  }, []);

  return {
    handleConfirmUpdate,
    handleDeleteStacks,
    handleBrazilInterpreterUpdate
  };
};
